import React, { useState } from 'react';
import './Menu.css';
import { Nav, Modal, Form } from 'react-bootstrap';
import { IoKeyOutline } from 'react-icons/io5';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { authService } from '../../services/AuthService';
import { getApiErrorMessage } from '../../utils/apiErrorUtils';

const PasswordField = ({
    label,
    value,
    onChange,
    error,
    showPassword,
    onToggleVisibility,
    autoComplete,
}) => (
    <Form.Group className="mb-3">
        <Form.Label>{label}</Form.Label>
        <div className="change-password-field-wrapper">
            <Form.Control
                type={showPassword ? 'text' : 'password'}
                value={value}
                onChange={onChange}
                isInvalid={!!error}
                autoComplete={autoComplete}
                className="change-password-input"
            />
            <button
                type="button"
                className="change-password-toggle"
                onClick={onToggleVisibility}
                aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
            >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
        </div>
        {error && (
            <div className="invalid-feedback d-block">
                {error}
            </div>
        )}
    </Form.Group>
);

const MenuChangePasswordButton = ({ isOpen, isMobile }) => {
    const [showModal, setShowModal] = useState(false);
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const resetForm = () => {
        setCurrentPassword('');
        setNewPassword('');
        setConfirmNewPassword('');
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmPassword(false);
        setErrors({});
        setIsSubmitting(false);
    };

    const handleClose = () => {
        setShowModal(false);
        resetForm();
    };

    const validateForm = () => {
        const formErrors = {};

        if (!currentPassword.trim()) {
            formErrors.currentPassword = 'Campo obligatorio';
        }

        if (!newPassword.trim()) {
            formErrors.newPassword = 'Campo obligatorio';
        } else if (newPassword.length < 8) {
            formErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres';
        }

        if (!confirmNewPassword.trim()) {
            formErrors.confirmNewPassword = 'Campo obligatorio';
        } else if (newPassword !== confirmNewPassword) {
            formErrors.confirmNewPassword = 'Las contraseñas no coinciden';
        }

        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            await authService.changePassword(currentPassword, newPassword, confirmNewPassword);
            toast.success('Contraseña actualizada correctamente');
            handleClose();
        } catch (error) {
            const message = getApiErrorMessage(error, 'No se pudo cambiar la contraseña');
            toast.error(message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const openModal = (event) => {
        event.preventDefault();
        setShowModal(true);
    };

    return (
        <>
            <div className={`${isMobile ? 'changePasswordMobile' : 'menu-item-container change-password-section'}`}>
                <Nav.Link onClick={openModal}>
                    <div>
                        <IoKeyOutline className="icon" />
                    </div>
                    <div className="span-container">
                        <span className={isOpen ? 'text' : 'text hidden'}>Cambiar contraseña</span>
                    </div>
                </Nav.Link>
            </div>

            <Modal
                dialogClassName="cpw-modal-dialog"
                show={showModal}
                onHide={handleClose}
                backdrop="static"
                keyboard={false}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title>Cambiar contraseña</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <p className="cpw-modal-description">
                            Actualizá tu contraseña de acceso. La nueva debe tener al menos 8 caracteres.
                        </p>

                        <PasswordField
                            label="Contraseña actual"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                            error={errors.currentPassword}
                            showPassword={showCurrentPassword}
                            onToggleVisibility={() => setShowCurrentPassword((prev) => !prev)}
                            autoComplete="current-password"
                        />

                        <PasswordField
                            label="Nueva contraseña"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            error={errors.newPassword}
                            showPassword={showNewPassword}
                            onToggleVisibility={() => setShowNewPassword((prev) => !prev)}
                            autoComplete="new-password"
                        />

                        <PasswordField
                            label="Confirmar nueva contraseña"
                            value={confirmNewPassword}
                            onChange={(e) => setConfirmNewPassword(e.target.value)}
                            error={errors.confirmNewPassword}
                            showPassword={showConfirmPassword}
                            onToggleVisibility={() => setShowConfirmPassword((prev) => !prev)}
                            autoComplete="new-password"
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            className="cpw-modal-btn cpw-modal-btn--secondary"
                            onClick={handleClose}
                            disabled={isSubmitting}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="cpw-modal-btn cpw-modal-btn--primary"
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Guardando...' : 'Guardar'}
                        </button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </>
    );
};

export default MenuChangePasswordButton;
